<script setup lang="ts">
import type { Schemas } from '#shopware'
import { getTranslatedProperty } from "@shopware/helpers"

const props = defineProps<{
    data: Schemas['Product'],
    limitWidth?: boolean,
    /**
     * Whether the product's campaign is currently running. Owned by the caller, which already
     * holds the one `useCampaignDeal` instance for this product — instantiating a second one here
     * would start a second order-count poll for the same card.
     */
    campaignActive?: boolean
}>()

/**** HIERARCHY
 multiDeal -> multiDeal43 -> promotion -> discount > black deal > limitedOffer > freeGift > sale
 pre-order is standalone, all other badges are ignored
****/

type BadgeKey = 'multiDeal' | 'multiDeal43' | 'promotion' | 'isDiscount' | 'blackDeal' | 'limitedOffer' | 'hasGift' | 'isSale'

const BADGE_CONFIG: Record<string, { class: string, dataCy: string, i18nKey: string }> = {
    multiDeal: {
        class: 'badge badge-accent rounded-none',
        dataCy: 'multi-deal-badge',
        i18nKey: 'product.listing.card.badges.multiDeal',
    },
    multiDeal43: {
        class: 'badge badge-accent rounded-none',
        dataCy: 'multi-deal-43-badge',
        i18nKey: 'product.listing.card.badges.multiDeal43',
    },
    promotion: {
        class: 'badge badge-sale rounded-none',
        dataCy: 'promotion-badge',
        i18nKey: 'product.listing.card.badges.promotion',
    },
    blackDeal: {
        class: 'badge badge-dark rounded-none',
        dataCy: 'black-deal-badge',
        i18nKey: 'product.listing.card.badges.blackDeal',
    },
    limitedOffer: {
        class: 'badge badge-limited rounded-none whitespace-nowrap',
        dataCy: 'limited-offer-badge',
        i18nKey: 'product.listing.card.badges.limitedOffer',
    },
    hasGift: {
        class: 'badge badge-accent rounded-none',
        dataCy: 'free-gift-badge',
        i18nKey: 'product.listing.card.badges.hasGift',
    },
    isSale: {
        class: 'badge badge-sale rounded-none',
        dataCy: 'sale-badge',
        i18nKey: 'product.listing.card.badges.isSale',
    },
}

const HIERARCHY: BadgeKey[] = ['multiDeal', 'multiDeal43', 'promotion', 'isDiscount', 'blackDeal', 'limitedOffer', 'hasGift', 'isSale']

const badges = computed(() => props.data?.extensions?.badges)
const customFields = computed<Schemas['Product']['customFields']>(() => getTranslatedProperty(props.data, 'customFields') as Schemas['Product']['customFields'])

function isDiscount(input: number | null | undefined) {
    return (input != null && input > 0)
}

function isBadgeActive(key: BadgeKey): boolean {
    const b = badges.value
    switch (key) {
    case 'multiDeal':    return !!b?.multiDeal
    case 'multiDeal43':  return !!b?.multiDeal43
    case 'promotion':    return !!b?.promotion
    case 'isDiscount':   return isDiscount(b?.discount)
    case 'blackDeal':    return !!b?.blackDeal
    case 'limitedOffer': return !!b?.limitedOffer
    case 'hasGift':      return !!props.data?.hasGifts
    case 'isSale':       return !!b?.sale
    }
}

const visibleBadges = computed((): BadgeKey[] =>
    HIERARCHY.filter(isBadgeActive).slice(0, 2) // at most 2 badges shown: primary + secondary
)

function formatDiscount(discount: number | null | undefined) {
    return discount?.toFixed(0) ?? ''
}

/**
 * The campaign badge is deliberately rendered outside the `HIERARCHY` + `.slice(0, 2)` budget:
 * it marks an active promotion (a different kind of thing than the product-attribute badges in
 * `BADGE_CONFIG`), and the design always shows it alongside the Sale badge rather than letting it
 * compete for one of the two slots — so it must never displace a higher-priority badge.
 */
const promotionDetails = computed(() => props.data?.extensions?.promotionDetails)

const campaignBadgeText = computed(() =>
    promotionDetails.value?.campaignBadgeText || promotionDetails.value?.campaignTitle || ''
)

// Gated on the campaign actually running, not just on the field being set: a page served from
// cache can outlive its promotion, and a badge left standing while the bars have already gone
// would be exactly the stale deal messaging the ticket rules out.
const isCampaignBadgeVisible = computed(() =>
    props.campaignActive === true
    && !!promotionDetails.value?.campaignShowBadge
    && campaignBadgeText.value !== ''
)

const campaignColor = computed(() => promotionDetails.value?.campaignColor ?? undefined)
const { badgeStyle: campaignBadgeStyle } = useCampaignTheme(campaignColor)
</script>

<template>
    <div
        class="absolute flex left-2.5"
        :class="[
            (badges?.discount && !limitWidth) ? 'flex-col-reverse gap-3' : 'gap-2',
            {
                'flex-row-reverse gap-2 justify-end': data?.hasGifts && badges?.sale,
                'flex-col-reverse gap-3': data?.hasGifts && badges?.limitedOffer && !limitWidth,
                'w-11/12': !limitWidth
            }
        ]"
    >
        <template v-if="customFields?.pre_orderable && customFields?.pre_order_delivery_time">
            <div
                class="badge rounded-none text-base-100 bg-secondary-content border-secondary-content px-2 text-center"
                data-cy="pre-order-badge"
            >
                {{ $t('product.listing.card.badges.preOrder') }}
            </div>
        </template>
        <template v-else>
            <template v-for="badge in visibleBadges" :key="badge">
                <template v-if="badge === 'isDiscount'">
                    <div
                        class="flex items-center"
                        :class="[limitWidth ? 'w-32' : 'w-full']"
                    >
                        <div
                            class="badge rounded-none text-primary-content bg-secondary-content border-secondary-content px-2 w-1/2 text-center"
                            data-cy="discount-badge"
                        >
                            {{ $t('product.listing.card.badges.coupon') }}
                        </div>
                        <div class="badge rounded-none text-primary-content bg-sale border-sale px-2 w-1/2 text-center">
                            {{ formatDiscount(badges?.discount) }}%
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div
                        :class="BADGE_CONFIG[badge]!.class"
                        :data-cy="BADGE_CONFIG[badge]!.dataCy"
                    >
                        {{ $t(BADGE_CONFIG[badge]!.i18nKey) }}
                    </div>
                </template>
            </template>

            <div
                v-if="isCampaignBadgeVisible"
                class="badge badge-dark rounded-none"
                :style="campaignBadgeStyle"
                data-cy="campaign-badge"
            >
                {{ campaignBadgeText }}
            </div>
        </template>
    </div>
</template>
